import React, { ReactNode } from 'react';
import Layout from '@/components/layout/Layout';
import { useLayoutPresence } from '@/context/LayoutContext';

interface LayoutWrapperProps {
  children: ReactNode;
}

/**
 * LayoutWrapper - Centralized layout management for routing
 * 
 * IMPORTANT: This component should ONLY be used in routing configuration (MainRoutes.tsx).
 * Page components should NEVER include <Layout> internally.
 * 
 * Correct pattern:
 * <Route path="/page" element={
 *   <LayoutWrapper>
 *     <PageComponent />
 *   </LayoutWrapper>
 * } />
 * 
 * For multilingual pages:
 * <Route path="/:lang/page" element={
 *   <LayoutWrapper>
 *     <MultilingualPageWrapper>
 *       <PageComponent />
 *     </MultilingualPageWrapper>
 *   </LayoutWrapper>
 * } />
 */
const LayoutWrapper: React.FC<LayoutWrapperProps> = ({ children }) => {
  const { hasLayout } = useLayoutPresence();
  
  // Safeguard: if we're already inside a Layout, just return children
  if (hasLayout) {
    if (process.env.NODE_ENV === 'development') {
      throw new Error(
        '❌ NESTED LAYOUT DETECTED!\n\n' +
        'A page component is trying to use <Layout> internally, but it\'s already wrapped by LayoutWrapper.\n' +
        'SOLUTION: Remove the <Layout> import and wrapper from the page component.\n' +
        'Pages should NEVER include <Layout> - only LayoutWrapper in routes should apply it.\n\n' +
        'Check your browser console for the component stack trace.'
      );
    }
    // In production, silently return children to avoid breaking the app
    return <>{children}</>;
  }
  
  return <Layout>{children}</Layout>;
};

export default LayoutWrapper;
